import { Stack, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { Navigate } from "react-router-dom";
import { Loading } from "components/Loading";
import { clearStoredToken } from "services/coreapi";
import { useAuthStore } from "stores/AuthStoreProvider";

const LOGIN_ROUTE = "/login";

/**
 * `/logout`: sign out, then go to the login page. A link support can send to
 * anyone who is stuck — onboarding that cannot finish, a session the platform
 * API keeps rejecting — and who cannot reach the sign-out button. `RouteGuard`
 * lets this route through in every auth state, so no gate can capture it.
 *
 * Mounts only once the auth store has settled (the guard and provider render
 * nothing while it initialises), so sign-out never races the session check.
 * Signed-out visitors just land on login, with any leftover token dropped.
 */
export const LogoutPage = observer(() => {
  const authStore = useAuthStore();
  const [done, setDone] = React.useState(false);
  // Run once, even under StrictMode's double effect.
  const started = React.useRef(false);

  React.useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!authStore.isAuthenticated) {
      clearStoredToken();
      setDone(true);
      return;
    }

    // Never rejects: the local token and user are cleared whatever the
    // server says.
    void authStore.logout().then(() => setDone(true));
  }, [authStore]);

  if (done) {
    return <Navigate to={LOGIN_ROUTE} replace={true} />;
  }

  return (
    <Stack
      minH={"100vh"}
      alignItems={"center"}
      justifyContent={"center"}
      gap={4}
    >
      <Loading />
      <Text color="whiteAlpha.700">Signing you out…</Text>
    </Stack>
  );
});
